import React, { useMemo, useState } from "react";
import useStore from "../../../useStore";
import { useToast } from "../../../Contexts/Toast/toast.provider";
import { useModal } from "../../../Hooks/useModal";
import FormSeccion from "./FormSeccion";
import FormProducto from "./FormProducto";
import Confirmar from "./Confirmar";
import { Switch, IconButton } from "./Campos";

const normalizar = (t) =>
  String(t || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

const porOrden = (a, b) => (a.orden ?? 0) - (b.orden ?? 0);

const precioTexto = (p) => (p.precio == null ? null : `$${p.precio}`);

function FilaProducto({ producto, acciones }) {
  const agotado = producto.disponible === false;
  return (
    <li className={`pp-producto ${agotado ? "pp-agotado" : ""}`}>
      <div className="pp-producto-info">
        <span className="pp-producto-nombre">
          {producto.nombre}
          {agotado && <span className="pp-badge pp-badge-rojo">Agotado</span>}
        </span>
        {producto.descripcion && <span className="pp-producto-desc">{producto.descripcion}</span>}
      </div>
      <span className={`pp-producto-precio ${producto.precio == null ? "sin-precio" : ""}`}>
        {precioTexto(producto) || "precio de sección"}
      </span>
      <div className="pp-producto-acciones">
        <Switch
          compacto
          label={agotado ? "Agotado" : "Disponible"}
          checked={!agotado}
          onChange={() => acciones.toggleDisponible(producto)}
        />
        <div className="pp-botonera">
          <IconButton icon="bi-pencil" title="Editar" onClick={() => acciones.editarProducto(producto)} />
          <IconButton icon="bi-trash3" title="Eliminar" variante="peligro" onClick={() => acciones.eliminarProducto(producto)} />
        </div>
      </div>
    </li>
  );
}

function TarjetaSeccion({ seccion, productos, hijos, todosProductos, acciones, filtro, nivel = 0 }) {
  const [abierta, setAbierta] = useState(true);
  const oculta = seccion.activa === false;
  const visibles = filtro ? productos.filter((p) => normalizar(`${p.nombre} ${p.descripcion}`).includes(filtro)) : productos;
  const expandida = abierta || Boolean(filtro);

  // Al buscar, las subsecciones sin coincidencias no se muestran
  if (filtro && nivel && !visibles.length) return null;

  return (
    <article className={`pp-seccion ${nivel ? "pp-subseccion" : ""} ${oculta ? "oculta" : ""}`}>
      <header className="pp-seccion-header">
        <button
          type="button"
          className="pp-seccion-toggle"
          onClick={() => setAbierta((a) => !a)}
          aria-expanded={expandida}
          title={expandida ? "Contraer" : "Expandir"}
        >
          <i className={`bi ${expandida ? "bi-chevron-down" : "bi-chevron-right"}`} />
        </button>
        <div className="pp-seccion-titulo">
          {seccion.eyebrow && !nivel && <small>{seccion.eyebrow}</small>}
          <h3>
            {seccion.nombre}
            {oculta && <span className="pp-badge">Oculta</span>}
          </h3>
          <span className="pp-seccion-meta">
            {productos.length} producto{productos.length === 1 ? "" : "s"}
            {(seccion.precios || []).length > 0 && ` · desde $${Math.min(...seccion.precios.map((p) => p.precio))}`}
            {hijos.length > 0 && ` · ${hijos.length} subsección${hijos.length === 1 ? "" : "es"}`}
          </span>
        </div>
        <div className="pp-seccion-acciones">
          <Switch compacto label={oculta ? "Oculta" : "Visible"} checked={!oculta} onChange={() => acciones.toggleSeccion(seccion)} />
          <div className="pp-botonera">
            <IconButton icon="bi-pencil" title="Editar sección" onClick={() => acciones.editarSeccion(seccion)} />
            <IconButton icon="bi-trash3" title="Eliminar sección" variante="peligro" onClick={() => acciones.eliminarSeccion(seccion, productos.length, hijos.length)} />
          </div>
        </div>
      </header>

      {expandida && (
        <div className="pp-seccion-cuerpo">
          {visibles.length > 0 && (
            <ul className="pp-productos">
              {visibles.map((p) => (
                <FilaProducto
                  key={p.productoId}
                  producto={p}
                  acciones={acciones}
                />
              ))}
            </ul>
          )}

          {!filtro && (
            <div className="pp-seccion-agregar">
              <button type="button" onClick={() => acciones.nuevoProducto(seccion.seccionId)}>
                <i className="bi bi-plus-lg" /> Producto
              </button>
              {!nivel && (
                <button type="button" onClick={() => acciones.nuevaSeccion(seccion.seccionId)}>
                  <i className="bi bi-folder-plus" /> Subsección
                </button>
              )}
            </div>
          )}

          {hijos.map((h) => (
            <TarjetaSeccion
              key={h.seccionId}
              seccion={h}
              productos={todosProductos.filter((p) => p.seccionId === h.seccionId).sort(porOrden)}
              hijos={[]}
              todosProductos={todosProductos}
              acciones={acciones}
              filtro={filtro}
              nivel={1}
            />
          ))}
        </div>
      )}
    </article>
  );
}

const AdminMenu = () => {
  const showToast = useToast();
  const { abrirModal } = useModal();
  const {
    secciones,
    productos,
    guardarProducto,
    guardarSeccion,
    eliminarProducto,
    eliminarSeccion,
  } = useStore();
  const [busqueda, setBusqueda] = useState("");
  const filtro = normalizar(busqueda.trim());

  const principales = useMemo(() => secciones.filter((s) => !s.padreId).sort(porOrden), [secciones]);
  const hijosDe = (id) => secciones.filter((s) => s.padreId === id).sort(porOrden);
  const productosDe = (id) => productos.filter((p) => p.seccionId === id).sort(porOrden);

  // Con búsqueda: solo secciones con coincidencias (o con subsecciones que las tengan)
  const coincide = (id) => productosDe(id).some((p) => normalizar(`${p.nombre} ${p.descripcion}`).includes(filtro));
  const visibles = filtro
    ? principales.filter((s) => coincide(s.seccionId) || hijosDe(s.seccionId).some((h) => coincide(h.seccionId)))
    : principales;

  const avisar = (r, okMsg) => showToast(r.ok ? "success" : "error", r.ok ? okMsg : r.message);

  const acciones = {
    nuevaSeccion: (padreId) =>
      abrirModal({
        title: padreId ? "Nueva subsección" : "Nueva sección",
        contenido: (cerrar) => <FormSeccion padreIdInicial={padreId} cerrar={cerrar} />,
      }),
    editarSeccion: (seccion) =>
      abrirModal({
        title: `Editar: ${seccion.nombre}`,
        contenido: (cerrar) => <FormSeccion seccion={seccion} cerrar={cerrar} />,
      }),
    toggleSeccion: async (s) => {
      const r = await guardarSeccion({ activa: s.activa === false }, s.seccionId);
      avisar(r, s.activa === false ? `“${s.nombre}” visible 👀` : `“${s.nombre}” oculta 🙈`);
    },
    eliminarSeccion: (s, nProductos, nHijos) => {
      if (nHijos) return showToast("warning", "Primero elimina o mueve sus subsecciones");
      if (nProductos) return showToast("warning", `Tiene ${nProductos} producto(s). Elimínalos o muévelos primero.`);
      abrirModal({
        title: "Eliminar sección",
        size: "small",
        contenido: (cerrar) => (
          <Confirmar
            mensaje={`¿Eliminar la sección “${s.nombre}”?`}
            detalle="Esta acción no se puede deshacer."
            cerrar={cerrar}
            onConfirm={async () => {
              const r = await eliminarSeccion(s.seccionId);
              avisar(r, "Sección eliminada 🗑️");
              return r.ok;
            }}
          />
        ),
      });
    },
    nuevoProducto: (seccionId) =>
      abrirModal({
        title: "Nuevo producto",
        size: "medium",
        contenido: (cerrar) => <FormProducto seccionIdInicial={seccionId} cerrar={cerrar} />,
      }),
    editarProducto: (producto) =>
      abrirModal({
        title: `Editar: ${producto.nombre}`,
        size: "medium",
        contenido: (cerrar) => <FormProducto producto={producto} cerrar={cerrar} />,
      }),
    toggleDisponible: async (p) => {
      const disponible = p.disponible === false;
      const r = await guardarProducto({ disponible }, p.productoId);
      avisar(r, disponible ? `${p.nombre}: disponible ✅` : `${p.nombre}: agotado ⛔`);
    },
    eliminarProducto: (p) =>
      abrirModal({
        title: "Eliminar producto",
        size: "small",
        contenido: (cerrar) => (
          <Confirmar
            mensaje={`¿Eliminar “${p.nombre}”?`}
            detalle="Si solo se acabó, mejor márcalo como agotado."
            cerrar={cerrar}
            onConfirm={async () => {
              const r = await eliminarProducto(p.productoId);
              avisar(r, "Producto eliminado 🗑️");
              return r.ok;
            }}
          />
        ),
      }),
  };

  return (
    <section className="pp-panel">
      <div className="pp-toolbar">
        <div className="pp-buscador">
          <i className="bi bi-search" />
          <input
            type="search"
            value={busqueda}
            placeholder="Buscar producto…"
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </div>
        <div className="pp-toolbar-botones">
          <button type="button" className="pp-btn pp-btn-sec" onClick={() => acciones.nuevaSeccion(null)}>
            <i className="bi bi-folder-plus" /> Sección
          </button>
          <button type="button" className="pp-btn" onClick={() => acciones.nuevoProducto(principales[0]?.seccionId)}>
            <i className="bi bi-plus-lg" /> Producto
          </button>
        </div>
      </div>

      {filtro && !visibles.length && <p className="pp-vacio">No hay productos que coincidan con “{busqueda}”.</p>}
      {!filtro && !principales.length && (
        <p className="pp-vacio">Aún no hay secciones. Crea la primera con el botón “Sección”.</p>
      )}

      {visibles.map((s) => (
        <TarjetaSeccion
          key={s.seccionId}
          seccion={s}
          productos={productosDe(s.seccionId)}
          hijos={hijosDe(s.seccionId)}
          todosProductos={productos}
          acciones={acciones}
          filtro={filtro}
        />
      ))}
    </section>
  );
};

export default AdminMenu;
